import { useCallback, useMemo, useSyncExternalStore } from 'react';
import {
  TenantTheme,
  ThemeCSSVariables,
} from '@asset-metrix/portal-gateway-client-js';
import { useLocalStorage } from '@mantine/hooks';
import convert from 'color-convert';
import { scheme } from 'vega';
import { z } from 'zod';

import { BASE_FONT_SIZE } from '../utils/pixelSizeHelper';

export const COLOR_SCHEME_STORAGE_KEY = 'color-scheme';

const LocalStorageColorSchemeSchema = z.literal(['light', 'dark']).optional();
type LocalStorageColorScheme = z.infer<typeof LocalStorageColorSchemeSchema>;
export type ColorScheme = NonNullable<LocalStorageColorScheme>;

export type SetColorSchemeOptions = {
  persist?: boolean;
};

type ColorSchemePreview = ColorScheme | null;

let colorSchemePreview: ColorSchemePreview = null;
const colorSchemePreviewListeners = new Set<() => void>();

function subscribeColorSchemePreview(listener: () => void) {
  colorSchemePreviewListeners.add(listener);
  return () => {
    colorSchemePreviewListeners.delete(listener);
  };
}

function getColorSchemePreview() {
  return colorSchemePreview;
}

function setColorSchemePreview(value: ColorSchemePreview) {
  colorSchemePreview = value;
  for (const listener of colorSchemePreviewListeners) {
    listener();
  }
}

export function discardColorSchemePreview() {
  setColorSchemePreview(null);
}

/**
 * Drops the persisted color scheme so the next user of this browser starts from
 * the tenant default. Mounted `useLocalStorage` hooks keep their value in React
 * state, so they have to be notified through Mantine's storage event.
 */
export function clearStoredColorScheme() {
  discardColorSchemePreview();

  try {
    globalThis.localStorage.removeItem(COLOR_SCHEME_STORAGE_KEY);
  } catch (error) {
    console.warn('Could not clear the stored color scheme', error);
    return;
  }

  globalThis.dispatchEvent(
    new CustomEvent('mantine-local-storage', {
      detail: { key: COLOR_SCHEME_STORAGE_KEY, value: undefined },
    })
  );
}

type RGB = [number, number, number];

const THEME_CSS_VARIABLES: (keyof ThemeCSSVariables)[] = [
  '--portal-ui-color-primary',
  '--portal-ui-color-secondary',
  '--portal-ui-font-family',
  '--portal-ui-logo',
  '--portal-ui-logo-small',
  '--portal-ui-background-image',
  '--portal-ui-favicon',
  '--portal-ui-logo-footer',
];

const DEFAULT_COLOR_SCHEME = 'light';

export function useColorScheme(
  defaultValue: LocalStorageColorScheme = DEFAULT_COLOR_SCHEME
) {
  const [storedColorScheme, setStoredColorScheme] =
    useLocalStorage<LocalStorageColorScheme>({
      getInitialValueInEffect: false,
      key: COLOR_SCHEME_STORAGE_KEY,
      defaultValue,
      deserialize: (value) => {
        try {
          return LocalStorageColorSchemeSchema.parse(JSON.parse(value ?? ''));
        } catch {
          return defaultValue;
        }
      },
    });

  const previewColorScheme = useSyncExternalStore(
    subscribeColorSchemePreview,
    getColorSchemePreview
  );

  const colorScheme =
    previewColorScheme ??
    storedColorScheme ??
    defaultValue ??
    DEFAULT_COLOR_SCHEME;

  const setColorScheme = useCallback(
    (value: ColorScheme, options: SetColorSchemeOptions = {}) => {
      const persist = options.persist ?? true;
      if (persist) {
        setColorSchemePreview(null);
        setStoredColorScheme(value);
        return;
      }

      setColorSchemePreview(value);
    },
    [setStoredColorScheme]
  );

  return [colorScheme, setColorScheme] as const;
}

export function usePortalThemeBase(themes: TenantTheme | null | undefined) {
  const [colorScheme] = useColorScheme(themes?.activeTheme);

  const activeTheme: ThemeCSSVariables | undefined = useMemo(
    () => themes?.[colorScheme],
    [colorScheme, themes]
  );

  return {
    colorScheme,
    activeTheme,
    userCanSwitchTheme: themes?.userCanSwitchTheme ?? false,
    tenantThemeLoaded: activeTheme != null,
  };
}

/* Called outside of React context. May not use any react specific features. */
export const applyThemeVariables = (
  activeTheme?: ThemeCSSVariables | null,
  colorScheme: TenantTheme['activeTheme'] = DEFAULT_COLOR_SCHEME,
  container: HTMLElement = document.body
) => {
  if (container) {
    const schemeClass = `portal-theme--${colorScheme}`;
    const otherSchemeClass = `portal-theme--${
      colorScheme === 'dark' ? 'light' : 'dark'
    }`;

    container.classList.add(schemeClass);
    container.classList.remove(otherSchemeClass);

    for (const cssVar of THEME_CSS_VARIABLES) {
      const value = activeTheme?.[cssVar];
      if (value && typeof value === 'string') {
        container.style.setProperty(cssVar, value);
      } else {
        container.style.removeProperty(cssVar);
      }
    }

    document.body.style.setProperty(
      '--portal-ui-base-font-size',
      `${BASE_FONT_SIZE}px`
    );

    document.body.classList.toggle(
      'with-bg-logo',
      !!activeTheme?.['--portal-ui-logo-footer']
    );
  }

  if (activeTheme) {
    const { '--portal-ui-color-primary': primaryColor } = activeTheme;
    const primaryColorRgb = primaryColor.split(',') as unknown as RGB;
    const [primaryColorH, primaryColorS, primaryColorL] =
      convert.rgb.hsl(primaryColorRgb);

    const colorsFull: string[] = activeTheme?.['vega-chart-color-schema'] || [];
    const gradientColors: string[] =
      activeTheme?.['vega-chart-gradient-colors'] || [];

    scheme(
      'AssetMetrix12',
      colorsFull.filter((_c, idx) => idx % 2 === 0)
    );

    scheme('AssetMetrix24', colorsFull);

    const singleHueBlack = (f: number) => {
      const b = 10;
      const a = primaryColorL - b;

      const H = primaryColorH;
      const S = primaryColorS;
      const L = Math.max(0, Math.min(100, a * f + b));
      return `#${convert.hsl.hex([H, S, L])}`;
    };

    const singleHueWhite = (f: number) => {
      const b = 90;
      const a = primaryColorL - b;

      const H = primaryColorH;
      const S = primaryColorS;
      const L = Math.max(0, Math.min(100, a * f + b));
      return `#${convert.hsl.hex([H, S, L])}`;
    };

    scheme('AssetMetrixSingleHueBlack', singleHueBlack);
    scheme('AssetMetrixSingleHueWhite', singleHueWhite);

    const multiHueColorSchemeGenerator = (inputValue: number) => {
      const numColors = gradientColors.length;
      const clamp = (a: number) => Math.min(numColors - 1, Math.max(a, 0));

      const scaledInput = inputValue * (numColors - 1);
      const lowerIndex = clamp(Math.floor(scaledInput));
      const upperIndex = clamp(Math.ceil(scaledInput));
      const lowerColor = convert.hex.rgb(gradientColors[lowerIndex]);
      const upperColor = convert.hex.rgb(gradientColors[upperIndex]);
      const weight = scaledInput - lowerIndex;

      const r = lowerColor[0] + (upperColor[0] - lowerColor[0]) * weight;
      const g = lowerColor[1] + (upperColor[1] - lowerColor[1]) * weight;
      const b = lowerColor[2] + (upperColor[2] - lowerColor[2]) * weight;

      return `#${convert.rgb.hex([r, g, b])}`;
    };

    scheme('multiHueColorScheme', multiHueColorSchemeGenerator);
  }
};
